import React, { useEffect, useState, useCallback } from "react";
import { fetchMyWorkspaces } from "@/lib/workspaceFetch";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Loader2, Bell, Mail } from "lucide-react";
import { toast } from "sonner";

// Same-origin in browser so the session cookie (scoped to the page's host) is sent on every API call.
// Falls back to the build-time env var only for SSR / localhost.
const _API_BASE = (typeof window !== 'undefined' && window.location.hostname !== 'localhost') ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${_API_BASE}/api`;

const TRIGGERS = [
  { key: "closed_won", label: "Closed-Won → engagement created", desc: "Email me when a deal moves to Closed-Won and an engagement is spawned." },
  { key: "session_reminder", label: "Session reminder (day-of)", desc: "9am reminder for any session scheduled today." },
  { key: "milestone_overdue", label: "Milestone overdue", desc: "Daily 9am alert for pending milestones past due date." },
  { key: "weekly_digest", label: "Weekly digest (Mon 8am)", desc: "Your engagements, sessions this week, and overdue milestones." },
];

export default function NotificationPrefsPage() {
  const [workspaceId, setWorkspaceId] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetchMyWorkspaces()
      .then((rows) => rows?.[0] && setWorkspaceId(rows[0].workspace_id))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/notification-prefs/me`, { credentials: "include" });
      if (r.ok) setPrefs(await r.json());
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (key, value) => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/notification-prefs/me`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [key]: value }),
      });
      if (!r.ok) throw new Error("Failed to update");
      setPrefs(await r.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/notifications/test`, {
        method: "POST", credentials: "include",
      });
      const d = await r.json();
      if (d.ok) toast.success(`Test email sent to ${d.to}`);
      else toast.error("Email send failed — please contact your admin to verify the email service");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="notification-prefs-page">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Bell className="h-6 w-6 text-blue-600" /> Notifications</h1>
          <p className="text-xs text-slate-500">Control which REX emails you receive. Sender: rex@askrex.info</p>
        </div>
        <Button onClick={sendTest} disabled={testing} variant="outline" data-testid="notif-test-btn">
          {testing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          <Mail className="h-4 w-4 mr-1" /> Send test
        </Button>
      </div>

      {loading || !prefs ? (
        <Card className="border-slate-200"><CardContent className="p-6 flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</CardContent></Card>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <ul className="divide-y divide-slate-100">
              {TRIGGERS.map((t) => (
                <li key={t.key} className="flex items-center gap-3 p-4" data-testid={`notif-pref-${t.key}`}>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800">{t.label}</p>
                    <p className="text-xs text-slate-500">{t.desc}</p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!prefs[t.key]}
                    disabled={saving}
                    onClick={() => toggle(t.key, !prefs[t.key])}
                    data-testid={`notif-toggle-${t.key}`}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${prefs[t.key] ? "bg-blue-600" : "bg-slate-300"}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${prefs[t.key] ? "translate-x-6" : "translate-x-1"}`} />
                  </button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
